import React, { useEffect, useState } from "react";
import { useParams, Link, useLocation } from "react-router-dom";
import axios from "axios";
import { API } from "@/lib/api";
import PublicLeadForm from "@/components/PublicLeadForm";
import InlinePresentationMedia from "@/components/InlinePresentationMedia";
import ProjectLayoutCatalogue from "@/components/ProjectLayoutCatalogue";
import { unitCellClass, formatPrice, statusChipStyle, statusStyle } from "@/lib/constants";

export default function PublicProjectPresentation() {
  const { id } = useParams();
  const location = useLocation();
  const [data, setData] = useState(null);
  const [blockId, setBlockId] = useState(null);
  const [galleryIndex, setGalleryIndex] = useState(null);
  const [inventoryOpen, setInventoryOpen] = useState(false);

  useEffect(() => {
    axios.get(`${API}/public/projects/${id}`).then(({ data }) => {
      setData(data);
      if (data.blocks?.length) setBlockId(data.blocks[0].id);
    });
  }, [id]);

  if (!data) return <div className="min-h-screen flex items-center justify-center bg-slate-50 text-slate-500">Yükleniyor…</div>;

  const { project, company, available_units, blocks, floors, all_units } = data;
  const primary = company?.primary_color || "#0F172A";
  const accent = company?.secondary_color || "#F59E0B";
  const inventoryFromLink = new URLSearchParams(location.search).get("inventory") === "1";
  const inventoryEnabled = project.public_inventory_enabled !== false;
  const showStock = project.public_show_stock !== false;
  const showPrices = project.public_show_prices === true;
  const showInventory = inventoryEnabled && (inventoryFromLink || inventoryOpen);

  const blockFloors = floors.filter((f) => f.block_id === blockId);
  const gallery = project.gallery_images || [];
  const activeGalleryUrl = galleryIndex != null ? gallery[galleryIndex] : null;
  const projectVideoEmbedUrl = getVideoEmbedUrl(project.video_url);
  const showPrevGallery = () => setGalleryIndex((i) => (i == null ? 0 : (i - 1 + gallery.length) % gallery.length));
  const showNextGallery = () => setGalleryIndex((i) => (i == null ? 0 : (i + 1) % gallery.length));
  const optionGroups = Object.values(
    all_units.reduce((acc, u) => {
      const layout = (u.layout_type || "").trim();
      const room = (u.room_type || "").trim();
      const name = layout
        ? (room && layout.toLocaleLowerCase("tr").startsWith(room.toLocaleLowerCase("tr")) ? layout : [room, layout].filter(Boolean).join(" "))
        : (room || u.unit_type || "Bölüm");
      if (!acc[name]) {
        acc[name] = {
          name,
          room_type: u.room_type,
          layout_type: u.layout_type,
          unit_type: u.unit_type,
          total: 0,
          statuses: {},
          min_price: null,
          max_price: null,
          currency: u.currency || "TRY",
          samples: [],
        };
      }
      const group = acc[name];
      group.total += 1;
      if (u.status) group.statuses[u.status] = (group.statuses[u.status] || 0) + 1;
      if (u.price) {
        const price = Number(u.price);
        group.min_price = group.min_price == null ? price : Math.min(group.min_price, price);
        group.max_price = group.max_price == null ? price : Math.max(group.max_price, price);
        group.currency = u.currency || group.currency;
      }
      if (group.samples.length < 3 && ["Satışta", "Güncellemede"].includes(u.status)) {
        group.samples.push(u);
      }
      return acc;
    }, {})
  ).sort((a, b) => (a.room_type || "").localeCompare(b.room_type || "", "tr") || a.name.localeCompare(b.name, "tr"));

  return (
    <div className="min-h-screen" data-testid="public-presentation-page" style={{ background: "#FAFAF7" }}>
      {/* Header */}
      <header className="bg-white border-b" style={{ borderColor: "#E5E7EB" }}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            {company?.logo_url ? (
              <img src={company.logo_url} alt="" className="h-10" />
            ) : (
              <div className="h-10 w-10 rounded-lg flex items-center justify-center text-white font-bold text-lg" style={{ background: primary }}>
                {company?.short_name?.[0] || company?.name?.[0] || "F"}
              </div>
            )}
            <div>
              <div className="text-base font-semibold" style={{ color: primary }} data-testid="public-company-name">{company?.name}</div>
              <div className="text-xs text-slate-500">{company?.presentation_title || ""}</div>
            </div>
          </div>
          <div className="text-xs text-slate-500 hidden md:flex items-center gap-3">
            {company?.phone && <span>{company.phone}</span>}
            {company?.email && <span>{company.email}</span>}
            {company?.website && <a href={company.website} target="_blank" rel="noreferrer" className="underline">{company.website}</a>}
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative" data-testid="public-hero">
        <div className="aspect-[21/9] w-full bg-slate-200 relative overflow-hidden">
          {project.cover_image_url && <img src={project.cover_image_url} alt={project.name} className="w-full h-full object-cover" />}
          <div className="absolute inset-0 presentation-hero" />
          <div className="absolute inset-0 max-w-7xl mx-auto px-6 flex flex-col justify-end pb-10">
            <div className="inline-flex items-center gap-2 mb-2">
              <span className="px-3 py-1 text-xs uppercase tracking-widest rounded-full text-white" style={{ background: accent }}>{project.status}</span>
              <span className="px-3 py-1 text-xs uppercase tracking-widest rounded-full bg-white/15 text-white backdrop-blur-sm">{project.project_type}</span>
            </div>
            <h1 className="text-4xl md:text-6xl font-bold text-white font-display max-w-3xl leading-tight" data-testid="public-project-title">{project.name}</h1>
            <div className="mt-3 text-white/90 text-sm md:text-base">
              {project.location} {project.delivery_date && `• Teslim: ${project.delivery_date}`}
            </div>
          </div>
        </div>
      </section>

      {/* Description */}
      <section className="max-w-7xl mx-auto px-6 py-10">
        <div className="grid md:grid-cols-3 gap-8">
          <div className="md:col-span-2 space-y-4">
            <h2 className="text-2xl font-bold text-slate-900 font-display">
              {project.presentation_title || "Proje Hakkında"}
            </h2>
            {project.presentation_description && (
              <p className="text-slate-800 leading-relaxed font-medium">{project.presentation_description}</p>
            )}
            <p className="text-slate-700 leading-relaxed">{project.short_description}</p>
            {project.long_description && <p className="text-slate-600 leading-relaxed whitespace-pre-line">{project.long_description}</p>}
            {(project.brochure_url || project.floor_plan_url || project.site_plan_url) && (
              <div className="pt-2" data-testid="public-media-links">
                <InlinePresentationMedia title="Broşür" urls={[project.brochure_url]} />
                <InlinePresentationMedia title="Kat Planı" urls={[project.floor_plan_url]} />
                <InlinePresentationMedia title="Vaziyet Planı" urls={[project.site_plan_url]} />
              </div>
            )}
          </div>
          <div className="bg-white border rounded-xl p-5 space-y-3" style={{ borderColor: "#E5E7EB" }}>
            {showInventory && showStock && (
              <>
                <Stat l="Toplam Bağımsız Bölüm" v={all_units.length} />
                <Stat l="Satıştaki Bölümler" v={available_units.length} />
                <Stat l="Blok / Etap" v={blocks.length} />
              </>
            )}
            <div className="pt-3 border-t" style={{ borderColor: "#E5E7EB" }}>
              <div className="text-xs uppercase tracking-wider text-slate-500">İletişim</div>
              <div className="mt-1 text-sm font-medium text-slate-900">{company?.phone}</div>
              <div className="text-sm text-slate-700">{company?.email}</div>
              {project.map_link && (
                <a href={project.map_link} target="_blank" rel="noreferrer" className="block mt-2 text-sm underline">Haritada gör</a>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Gallery + Video */}
      {(gallery.length > 0 || project.video_url) && (
        <section className="max-w-7xl mx-auto px-6 pb-10" data-testid="public-gallery">
          <div className="flex items-end justify-between gap-4 mb-4">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-slate-500">Görsel Sunum</div>
              <h2 className="text-2xl font-bold text-slate-900 font-display">Galeri ve Video</h2>
            </div>
          </div>
          <div className="grid lg:grid-cols-[1.2fr_0.8fr] gap-4">
            {gallery[0] && (
              <button type="button" onClick={() => setGalleryIndex(0)}
                      className="block min-h-[320px] bg-slate-100 rounded-xl overflow-hidden text-left">
                <img src={gallery[0]} alt="" className="w-full h-full object-cover hover:scale-105 transition duration-300" />
              </button>
            )}
            <div className="grid grid-cols-2 gap-3">
              {gallery.slice(1, 5).map((url, i) => (
                <button key={url} type="button" onClick={() => setGalleryIndex(i + 1)}
                        className="block aspect-[4/3] bg-slate-100 rounded-xl overflow-hidden text-left">
                  <img src={url} alt="" className="w-full h-full object-cover hover:scale-105 transition duration-300" />
                </button>
              ))}
            </div>
          </div>
          {project.video_url && (
            <div className="mt-4 bg-white border rounded-xl overflow-hidden" style={{ borderColor: "#E5E7EB" }}>
              <div className="px-4 py-3 text-sm font-semibold text-slate-900">Proje Tanıtım Videosu</div>
              <div className="aspect-video bg-slate-100">
                {projectVideoEmbedUrl ? (
                  <iframe src={projectVideoEmbedUrl} title="Proje tanıtım videosu" className="w-full h-full" allowFullScreen />
                ) : (
                  <video src={project.video_url} controls className="w-full h-full object-cover" />
                )}
              </div>
            </div>
          )}
        </section>
      )}

      <ProjectLayoutCatalogue options={data.layout_options || []} />

      {inventoryEnabled && !showInventory && (
        <section className="max-w-7xl mx-auto px-6 pb-10" data-testid="public-inventory-gate">
          <button type="button"
                  onClick={() => setInventoryOpen(true)}
                  className="w-full bg-white border rounded-xl p-6 text-left hover:shadow-md transition"
                  style={{ borderColor: "#E5E7EB" }}>
            <div className="text-xs uppercase tracking-[0.2em] text-slate-500">Stok Bilgisi</div>
            <div className="mt-1 text-2xl font-bold text-slate-900 font-display">Dairelerimiz ve Proje Envanteri</div>
            <p className="mt-2 text-sm text-slate-600 max-w-3xl">
              Projedeki daire seçeneklerini ve paylaşılmış envanter bilgilerini görüntüleyin.
            </p>
            <div className="mt-4 inline-flex px-4 py-2 rounded-lg text-sm font-semibold text-white" style={{ background: accent }}>
              Daire seçeneklerini göster
            </div>
          </button>
        </section>
      )}

      {showInventory && (
        <>
      {/* Unit options */}
      {optionGroups.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 pb-10" data-testid="public-unit-options">
          <div className="flex items-end justify-between gap-4 mb-4">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-slate-500">Daire Seçenekleri</div>
              <h2 className="text-2xl font-bold text-slate-900 font-display">{showStock ? "Plan Tipleri ve Stok Durumu" : "Plan Tipleri"}</h2>
            </div>
            {inventoryFromLink && showStock ? (
              <a href="#available-units" className="text-sm font-semibold underline" style={{ color: accent }}>Satıştaki bölümlere git</a>
            ) : (
              <button type="button"
                      onClick={() => setInventoryOpen(false)}
                      className="px-4 py-2 text-sm font-semibold border rounded-lg bg-white hover:bg-slate-50"
                      style={{ color: accent, borderColor: "#E5E7EB" }}>
                Daire seçeneklerini gizle
              </button>
            )}
          </div>
          <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
            {optionGroups.map((group) => (
              <div key={group.name} className="bg-white border rounded-xl p-5" style={{ borderColor: "#E5E7EB" }}>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="text-lg font-bold text-slate-900 font-display">{group.name}</div>
                    <div className="text-xs text-slate-500 mt-0.5">{group.unit_type || "Bağımsız Bölüm"}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-xs text-slate-500">Toplam</div>
                    <div className="text-xl font-bold text-slate-900">{group.total}</div>
                  </div>
                </div>
                {showStock && <div className="mt-4 grid grid-cols-3 gap-2 text-xs">
                  {["Satışta", "Rezerve", "Satıldı"].map((status) => {
                    const s = statusStyle(status);
                    return (
                      <div key={status} className="rounded-lg px-3 py-2" style={{ background: s.bg, color: s.text }}>
                        <div className="font-semibold">{s.label}</div>
                        <div className="text-lg font-bold leading-tight">{group.statuses[status] || 0}</div>
                      </div>
                    );
                  })}
                </div>}
                {showPrices && group.min_price != null && (
                  <div className="mt-4 pt-3 border-t" style={{ borderColor: "#E5E7EB" }}>
                    <div className="text-xs text-slate-500">Fiyat Aralığı</div>
                    <div className="text-base font-bold" style={{ color: accent }}>
                      {group.min_price === group.max_price
                        ? formatPrice(group.min_price, group.currency)
                        : `${formatPrice(group.min_price, group.currency)} - ${formatPrice(group.max_price, group.currency)}`}
                    </div>
                  </div>
                )}
                {group.samples.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {group.samples.map((u) => (
                      <Link key={u.id} to={`/p/units/${u.id}`}
                            className="px-2.5 py-1 text-xs border rounded-lg hover:border-amber-400"
                            style={{ borderColor: "#E5E7EB" }}>
                        {u.unit_no}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Status legend */}
      {showStock && <section className="max-w-7xl mx-auto px-6 pb-4">
        <div className="flex items-center gap-3 flex-wrap text-xs text-slate-600">
          {["Satışta", "Rezerve", "Kapora Alındı", "Satıldı", "Güncellemede", "Pasif"].map((status) => {
            const s = statusStyle(status);
            return (
              <span key={status} className="inline-flex items-center gap-1.5">
                <span className="w-3 h-3 rounded-sm" style={{ background: s.color }} /> {s.label}
              </span>
            );
          })}
          </div>
      </section>}

      {/* Stock map */}
      {showStock && <section className="max-w-7xl mx-auto px-6 pb-10">
        <h2 className="text-2xl font-bold text-slate-900 font-display mb-4">Kat Bazlı Stok Haritası</h2>
        <div className="flex gap-2 flex-wrap mb-4" data-testid="public-block-selector">
          {blocks.map((b) => (
            <button key={b.id} onClick={() => setBlockId(b.id)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition ${blockId === b.id ? "text-white" : "bg-white border border-slate-200 text-slate-700"}`}
                    style={blockId === b.id ? { background: primary } : {}}>
              {b.name}
            </button>
          ))}
        </div>
        <div className="space-y-3">
          {blockFloors.map((f) => {
            const fUnits = all_units.filter((u) => u.floor_id === f.id);
            return (
              <div key={f.id} className="bg-white border rounded-xl p-4" style={{ borderColor: "#E5E7EB" }}>
                <div className="text-sm font-semibold text-slate-900 mb-3">{f.name}</div>
                {fUnits.length > 0 ? (
                  <div className="grid gap-2 grid-cols-3 sm:grid-cols-5 lg:grid-cols-8">
                    {fUnits.map((u) => (
                      <Link key={u.id} to={`/p/units/${u.id}`}
                            className={`unit-cell ${unitCellClass(u.status)}`}
                            title={[u.unit_no, u.room_type, u.layout_type].filter(Boolean).join(" ")}>
                        <div className="text-sm font-bold leading-tight">{u.unit_no}</div>
                        <div className="text-[10px] opacity-90 mt-0.5">{[u.room_type, u.layout_type].filter(Boolean).join(" / ")}</div>
                      </Link>
                    ))}
                  </div>
                ) : (
                  <div className="rounded-lg border border-dashed border-slate-200 bg-slate-50 px-4 py-6 text-sm text-slate-500">
                    Bu katta sunuma açık bağımsız bölüm yok.
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </section>}

      {/* Available units */}
      {showStock && <section id="available-units" className="max-w-7xl mx-auto px-6 pb-12">
        <h2 className="text-2xl font-bold text-slate-900 font-display mb-4">Satıştaki Bölümler ({available_units.length})</h2>
        {available_units.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {available_units.slice(0, 24).map((u) => (
              <Link key={u.id} to={`/p/units/${u.id}`}
                    className="bg-white border rounded-xl p-5 hover:shadow-md transition" style={{ borderColor: "#E5E7EB" }}>
                <div className="flex items-start justify-between">
                  <div>
                    <div className="text-lg font-semibold text-slate-900">{u.unit_no}</div>
                    <div className="text-xs text-slate-500">{[u.unit_type, u.room_type, u.layout_type].filter(Boolean).join(" • ")}</div>
                  </div>
                  <span className="status-chip" style={statusChipStyle(u.status)}>
                    <span className="status-dot" style={{ background: statusStyle(u.status).color }} />{u.status}
                  </span>
                </div>
                <div className="mt-3 grid grid-cols-2 gap-2 text-xs text-slate-600">
                  <span>Net: <b className="text-slate-900">{u.net_area} m²</b></span>
                  <span>Brüt: <b className="text-slate-900">{u.gross_area} m²</b></span>
                  <span>Cephe: <b className="text-slate-900">{u.aspect}</b></span>
                  <span>Manzara: <b className="text-slate-900">{u.view}</b></span>
                  {u.layout_type && <span>Plan: <b className="text-slate-900">{u.layout_type}</b></span>}
                </div>
                {showPrices && <div className="mt-3 pt-3 border-t" style={{ borderColor: "#E5E7EB" }}>
                  <div className="text-xs text-slate-500">Fiyat</div>
                  <div className="text-xl font-bold" style={{ color: accent }}>{formatPrice(u.price, u.currency)}</div>
                </div>}
              </Link>
            ))}
          </div>
        ) : (
          <div className="bg-white border rounded-xl px-5 py-8 text-sm text-slate-500" style={{ borderColor: "#E5E7EB" }}>
            Şu anda satışta gösterilecek bağımsız bölüm yok.
          </div>
        )}
      </section>}
        </>
      )}

      <div className="max-w-7xl mx-auto px-4 py-8"><PublicLeadForm projectId={id} options={data.layout_options || []} /></div>
      <footer className="border-t bg-white" style={{ borderColor: "#E5E7EB" }}>
        <div className="max-w-7xl mx-auto px-6 py-6 text-center text-sm text-slate-500">
          {company?.presentation_footer || `© ${company?.name}`}
          <div className="text-xs text-slate-400 mt-1">Powered by İnşaat Paneli</div>
        </div>
      </footer>

      {activeGalleryUrl && (
        <div className="fixed inset-0 z-50 bg-slate-950/80 px-4 py-6 flex items-center justify-center"
             onClick={() => setGalleryIndex(null)}>
          <div className="relative w-full max-w-6xl" onClick={(e) => e.stopPropagation()}>
            <div className="bg-white rounded-xl overflow-hidden shadow-2xl">
              <div className="px-4 py-3 border-b flex items-center justify-between" style={{ borderColor: "#E5E7EB" }}>
                <div>
                  <div className="text-sm font-semibold text-slate-900">Proje Galerisi</div>
                  <div className="text-xs text-slate-500">{galleryIndex + 1} / {gallery.length}</div>
                </div>
                <button type="button" onClick={() => setGalleryIndex(null)}
                        className="px-3 py-1.5 text-sm border rounded-lg hover:bg-slate-50"
                        style={{ borderColor: "#E5E7EB" }}>
                  Kapat
                </button>
              </div>
              <div className="relative bg-slate-950">
                <img src={activeGalleryUrl} alt="" className="w-full max-h-[72vh] object-contain" />
                {gallery.length > 1 && (
                  <>
                    <button type="button" onClick={showPrevGallery}
                            className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/90 text-slate-900 text-2xl leading-none hover:bg-white"
                            aria-label="Önceki görsel">
                      ‹
                    </button>
                    <button type="button" onClick={showNextGallery}
                            className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/90 text-slate-900 text-2xl leading-none hover:bg-white"
                            aria-label="Sonraki görsel">
                      ›
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ l, v }) {
  return (
    <div className="flex items-baseline justify-between">
      <div className="text-sm text-slate-600">{l}</div>
      <div className="text-lg font-bold text-slate-900 font-display">{v}</div>
    </div>
  );
}

function getVideoEmbedUrl(url) {
  if (!url) return "";
  try {
    const parsed = new URL(url);
    if (parsed.hostname.includes("youtu.be")) {
      const id = parsed.pathname.replace("/", "");
      return id ? `https://www.youtube.com/embed/${id}` : "";
    }
    if (parsed.hostname.includes("youtube.com")) {
      if (parsed.pathname.startsWith("/embed/")) return url;
      const id = parsed.searchParams.get("v");
      return id ? `https://www.youtube.com/embed/${id}` : "";
    }
  } catch {
    return "";
  }
  return "";
}
